// frontend/src/modules/expenses/pages/ExpenseDetailPage.tsx
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Pencil, Trash2, CheckCircle2, XCircle, Wallet, Send, FileText, ListChecks, PlugZap,
} from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { useApi } from '../../../design-system/useApi';
import { useAuth } from '../../../core/auth/AuthProvider';
import { formatDate, formatDateTime, formatMoney } from '../../../design-system/format';
import { VISUAL } from '../../../design-system/visual';
import { Eyebrow, Avatar } from '../../../design-system/pds';
import { StatusBadge } from '../components/StatusBadge';
import { ReceiptUpload } from '../components/ReceiptUpload';
import { ApproveRejectDialog } from '../components/ApproveRejectDialog';
import { expensesApi } from '../api';
import { quickbooksApi } from '../../integrations/api';
import { useQuickBooksSync } from '../../integrations/useQuickBooksSync';
import { confirmRePush, reportPushResult } from '../../integrations/pushFeedback';
import type { Expense, ExpenseApproval } from '../types';

export function ExpenseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { identity } = useAuth();

  const expQ = useApi<{ expense: Expense }>(id ? `/api/expenses/${id}` : null);
  const approvalsQ = useApi<{ rows: ExpenseApproval[] }>(id ? `/api/expenses/${id}/approvals` : null);
  const qbo = useQuickBooksSync('expense', id ? [id] : []);
  const qboPushed = id ? qbo.isPushed(id) : false;
  const [expense, setExpense] = React.useState<Expense | null>(null);

  React.useEffect(() => { if (expQ.data) setExpense(expQ.data.expense); }, [expQ.data]);

  const [dialog, setDialog] = React.useState<'approve' | 'reject' | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);

  if (expQ.loading || !expense) return <LoadingState variant="page" />;

  const approvals = approvalsQ.data?.rows ?? [];
  const isOwner = identity?.type === 'user' && expense.user_id != null && identity.id === expense.user_id;
  const isManagerView = identity?.type === 'user' && !isOwner;
  const editableStatus = expense.status === 'submitted' || expense.status === 'draft';
  const canEdit = isOwner && editableStatus;
  const canDelete = isOwner && editableStatus;
  const canSubmit = isOwner && expense.status === 'draft';
  const canApproveReject = isManagerView && expense.status === 'submitted';
  const canReimburse = isManagerView && expense.status === 'approved';

  async function submitDraft() {
    setBusy('submit');
    try {
      const next = await expensesApi.submit(expense!.id);
      setExpense(next);
      toast.success('Expense submitted for approval');
    } catch (err: any) {
      toast.error('Submit failed', { description: err.message });
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (!(await confirm({
      title: 'Delete this expense?',
      description: 'This cannot be undone.',
      destructive: true,
      confirmLabel: 'Delete',
    }))) return;
    setBusy('delete');
    try {
      await expensesApi.remove(expense!.id);
      toast.success('Expense deleted');
      navigate('/dashboard/expenses');
    } catch (err: any) {
      toast.error('Delete failed', { description: err.message });
      setBusy(null);
    }
  }

  async function pushToQuickBooks() {
    if (qboPushed && !(await confirmRePush(confirm, 'expense'))) return;
    setBusy('quickbooks');
    try {
      const res = qboPushed
        ? await quickbooksApi.pushExpense(expense!.id, { force: true })
        : await quickbooksApi.pushExpense(expense!.id);
      reportPushResult(res);
      qbo.markPushed(expense!.id);
    } catch (err: any) {
      toast.error('Push to QuickBooks failed', { description: err.message });
    } finally {
      setBusy(null);
    }
  }

  async function reimburse() {
    setBusy('reimburse');
    try {
      const next = await expensesApi.markReimbursed(expense!.id);
      setExpense(next);
      toast.success('Marked as reimbursed');
    } catch (err: any) {
      toast.error('Failed to mark reimbursed', { description: err.message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow={
          <>
            <Link to="/dashboard/expenses" className="hover:text-foreground transition-colors">Expenses</Link>
            {' / '}
            <span className="font-mono">{expense.id.slice(0, 8)}</span>
          </>
        }
        title={expense.category_name}
        chip={<StatusBadge status={expense.status} />}
        description={
          <span>
            Submitted by <span className="font-semibold text-ink dark:text-white">{expense.employee_name}</span>
            {' · '}spent {formatDate(expense.spent_at)}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            {canSubmit && (
              <Button
                size="default"
                onClick={submitDraft}
                disabled={busy !== null}
                className="gap-1.5"
              >
                <Send className="h-3.5 w-3.5" />
                {busy === 'submit' ? 'Submitting…' : 'Submit'}
              </Button>
            )}
            {canEdit && (
              <Link to={`/dashboard/expenses/${expense.id}/edit`}>
                <Button variant="outline" size="default" className="gap-1.5">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              </Link>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="default"
                onClick={remove}
                disabled={busy !== null}
                className="gap-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-500/10"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {busy === 'delete' ? 'Deleting…' : 'Delete'}
              </Button>
            )}
            {canApproveReject && (
              <>
                <Button
                  variant="outline"
                  size="default"
                  onClick={() => setDialog('reject')}
                  className="gap-1.5"
                >
                  <XCircle className="h-3.5 w-3.5" /> Reject
                </Button>
                <Button
                  size="default"
                  onClick={() => setDialog('approve')}
                  className="gap-1.5"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                </Button>
              </>
            )}
            {canReimburse && (
              <Button
                size="default"
                onClick={reimburse}
                disabled={busy !== null}
                className="gap-1.5"
              >
                <Wallet className="h-3.5 w-3.5" />
                {busy === 'reimburse' ? 'Marking…' : 'Mark reimbursed'}
              </Button>
            )}
            {qbo.canPush && (
              <Button
                variant="outline"
                size="default"
                onClick={pushToQuickBooks}
                disabled={busy !== null}
                className="gap-1.5"
              >
                <PlugZap className="h-3.5 w-3.5" />
                {busy === 'quickbooks' ? 'Pushing…' : qboPushed ? 'Push again to QuickBooks' : 'Push to QuickBooks'}
              </Button>
            )}
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6 max-w-5xl">
        {/* Hero amount + timeline */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 110, damping: 22 }}
          className={[VISUAL.bento, 'p-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-end'].join(' ')}
        >
          <div>
            <Eyebrow className="mb-2">Amount</Eyebrow>
            <p className="text-stat-lg tabular-nums font-bold text-ink dark:text-white leading-none">
              {formatMoney(expense.amount, expense.currency)}
            </p>
            <p className="text-[13px] text-ink/55 dark:text-white/55 mt-2">
              {expense.status === 'reimbursed' && expense.reimbursed_at
                ? `Reimbursed ${formatDate(expense.reimbursed_at)}`
                : expense.status === 'approved' && expense.approved_at
                  ? `Approved ${formatDate(expense.approved_at)}`
                  : expense.status === 'rejected' && expense.rejected_at
                    ? `Rejected ${formatDate(expense.rejected_at)}`
                    : `Submitted ${formatDate(expense.submitted_at)}`}
            </p>
          </div>
          <ol className="flex items-center gap-0 self-stretch lg:self-end">
            <Step icon={<Send className="h-3.5 w-3.5" />} label="Submitted" date={formatDate(expense.submitted_at)} done />
            <StepDivider />
            <Step
              icon={<CheckCircle2 className="h-3.5 w-3.5" />}
              label={expense.status === 'rejected' ? 'Rejected' : 'Approved'}
              date={
                expense.status === 'rejected' && expense.rejected_at
                  ? formatDate(expense.rejected_at)
                  : expense.approved_at
                    ? formatDate(expense.approved_at)
                    : '—'
              }
              done={expense.status === 'approved' || expense.status === 'reimbursed' || expense.status === 'rejected'}
              tone={expense.status === 'rejected' ? 'destructive' : 'success'}
            />
            <StepDivider />
            <Step
              icon={<Wallet className="h-3.5 w-3.5" />}
              label="Reimbursed"
              date={expense.reimbursed_at ? formatDate(expense.reimbursed_at) : '—'}
              done={!!expense.reimbursed_at}
            />
          </ol>
        </motion.section>

        {/* Detail grid */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <DetailField label="Category" value={expense.category_name} />
          <DetailField label="Spent on" value={formatDate(expense.spent_at)} />
          <DetailField label="Currency" value={expense.currency} mono />
          <DetailField
            label="Employee"
            value={
              <div className="flex items-center gap-2.5">
                <Avatar name={expense.employee_name} size="sm" />
                <span className="text-[14px] font-semibold text-ink dark:text-white">{expense.employee_name}</span>
              </div>
            }
          />
          <DetailField label="Submitted" value={formatDateTime(expense.submitted_at)} />
          {expense.approved_at && (
            <DetailField label="Approved" value={formatDateTime(expense.approved_at)} />
          )}
        </section>

        {/* Description */}
        {expense.description && (
          <section className={[VISUAL.bento, 'p-6'].join(' ')}>
            <Eyebrow className="mb-2">Description</Eyebrow>
            <p className="text-[14px] text-ink dark:text-white leading-relaxed whitespace-pre-wrap">
              {expense.description}
            </p>
          </section>
        )}

        {/* Rejection reason */}
        {expense.status === 'rejected' && expense.rejected_reason && (
          <section className="p-6 rounded-3xl bg-rose-50 dark:bg-rose-500/10 ring-1 ring-rose-500/20">
            <Eyebrow className="mb-2 text-rose-700 dark:text-rose-300">Rejected because</Eyebrow>
            <p className="text-[14px] text-rose-900 dark:text-rose-100 leading-relaxed whitespace-pre-wrap">
              {expense.rejected_reason}
            </p>
          </section>
        )}

        {/* Approval trail */}
        {(approvals.length > 0 || expense.approval_chain_id) && (
          <section className={[VISUAL.bento, 'p-6'].join(' ')}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <Eyebrow>Approval trail</Eyebrow>
                <p className="text-[13px] text-ink/55 dark:text-white/55 mt-1">
                  {expense.approval_chain_id
                    ? `Multi-level chain — currently at level ${expense.current_approval_level + 1}`
                    : 'Single-step approval'}
                  {expense.reimbursable && ' · flagged for payroll reimbursement'}
                </p>
              </div>
              <ListChecks className="h-4 w-4 text-ink/40 dark:text-white/40" />
            </div>
            {approvals.length === 0 ? (
              <p className="text-[13px] text-ink/45 dark:text-white/45 italic">No decisions recorded yet.</p>
            ) : (
              <ul className="space-y-2.5">
                {approvals.map((a) => (
                  <li
                    key={a.id}
                    className="flex items-start gap-3 p-3 rounded-2xl bg-white/50 dark:bg-white/[0.04] ring-1 ring-ink/6 dark:ring-white/8"
                  >
                    <span
                      className={[
                        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                        a.decision === 'approved'
                          ? 'bg-emerald-500/15 text-emerald-600'
                          : 'bg-rose-500/15 text-rose-600',
                      ].join(' ')}
                    >
                      {a.decision === 'approved'
                        ? <CheckCircle2 className="h-3.5 w-3.5" />
                        : <XCircle className="h-3.5 w-3.5" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-ink dark:text-white">
                        Level {a.level + 1} · {a.decision === 'approved' ? 'Approved' : 'Rejected'}
                        <span className="ml-2 text-[12px] font-normal text-ink/50 dark:text-white/50 tabular-nums">
                          {formatDateTime(a.decided_at)}
                        </span>
                      </p>
                      {a.note && (
                        <p className="text-[13px] text-ink/65 dark:text-white/65 mt-0.5 whitespace-pre-wrap">{a.note}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Attachments */}
        <section className={[VISUAL.bento, 'p-6'].join(' ')}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <Eyebrow>Receipts</Eyebrow>
              <p className="text-[13px] text-ink/55 dark:text-white/55 mt-1">
                {expense.attachments.length === 0
                  ? 'No receipts attached'
                  : `${expense.attachments.length} file${expense.attachments.length === 1 ? '' : 's'} on this expense`}
              </p>
            </div>
            <FileText className="h-4 w-4 text-ink/40 dark:text-white/40" />
          </div>
          {/* readOnly hides ReceiptUpload's remove button entirely, so no
              onRemoveAttachment handler is wired here — receipts are managed
              from the edit page. */}
          <ReceiptUpload attachments={expense.attachments} readOnly />
          {expense.attachments.length === 0 && (
            <p className="text-[13px] text-ink/45 dark:text-white/45 italic mt-2">
              Nothing attached. Add a receipt by editing this expense.
            </p>
          )}
        </section>
      </div>

      {dialog && (
        <ApproveRejectDialog
          open={dialog !== null}
          onOpenChange={(o) => { if (!o) setDialog(null); }}
          mode={dialog}
          expense={expense}
          onDone={(next) => { setExpense(next); approvalsQ.reload(); }}
        />
      )}
    </div>
  );
}

/* ───────────────────────────── inner bits ───────────────────────────── */

function DetailField({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className={[VISUAL.bento, 'p-5'].join(' ')}>
      <Eyebrow className="mb-2">{label}</Eyebrow>
      <div className={[mono ? 'font-mono' : '', 'text-[14px] font-semibold text-ink dark:text-white'].join(' ')}>
        {value}
      </div>
    </div>
  );
}

function Step({
  icon, label, date, done, tone = 'success',
}: {
  icon: React.ReactNode;
  label: string;
  date: string;
  done: boolean;
  tone?: 'success' | 'destructive';
}) {
  const doneCls =
    tone === 'destructive'
      ? 'bg-rose-500 text-white ring-rose-500/40'
      : 'bg-lime-neon text-ink ring-lime-neon/40';
  return (
    <li className="flex items-center gap-2.5">
      <span
        className={[
          'inline-flex h-9 w-9 items-center justify-center rounded-full ring-1',
          done
            ? doneCls
            : 'bg-white/60 dark:bg-white/[0.05] text-ink/55 dark:text-white/55 ring-ink/10 dark:ring-white/15',
        ].join(' ')}
      >
        {done ? <CheckCircle2 className="h-4 w-4" /> : icon}
      </span>
      <div>
        <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink/55 dark:text-white/55">{label}</p>
        <p className="text-[12.5px] font-semibold text-ink dark:text-white tabular-nums">{date}</p>
      </div>
    </li>
  );
}

function StepDivider() {
  return <span aria-hidden className="mx-3 h-px w-6 sm:w-10 bg-ink/15 dark:bg-white/15" />;
}
